import { SETTINGS_NODES, type SettingsNode } from '~/shared/api/settings-api';
import { holds } from '~/shared/lib/permission-implications';

export interface SectionFunction {
  readonly permission: string;
  readonly href?: string;
}

export interface Section {
  readonly screen: string;
  readonly functions?: Readonly<Record<string, SectionFunction>>;
}

export const SECTIONS = {
  '/dashboard': {
    screen: 'dashboard:view',
    functions: {
      assignTasks: { permission: 'task:assign' },
    },
  },
  '/schedule': {
    screen: 'schedule:manage',
    functions: {
      view: { permission: 'schedule:view' },
      book: { permission: 'booking:create' },
      courts: { permission: 'catalog:manage' },
      tournament: { permission: 'tournament:manage' },
    },
  },
  '/clients': {
    screen: 'client:manage',
    functions: {
      blacklist: { permission: 'client:blacklist', href: '/clients/blacklist' },
      broadcast: { permission: 'notification:broadcast', href: '/clients/broadcast' },
      category: { permission: 'client:manage_category' },
      forget: { permission: 'client:forget' },
      seedRating: { permission: 'rating:seed_level' },
      voidMatch: { permission: 'rating:manage' },
      chat: { permission: 'chat:respond' },
      ownAudience: { permission: 'notification:audience_own' },
      viewAny: { permission: 'client:view_any' },
    },
  },
  '/guests': { screen: 'client:manage' },
  '/moderation/reports': { screen: 'client:manage' },
  '/bookings': {
    screen: 'booking:view_any',
    functions: {
      manage: { permission: 'booking:manage' },
      cancelOwn: { permission: 'booking:cancel_own' },
      cancelAny: { permission: 'booking:cancel_any' },
      createForOthers: { permission: 'booking:create_for_others' },
      switchFormat: { permission: 'booking:switch_training_format' },
      message: { permission: 'notification:send' },
      serviceNotice: { permission: 'notification:service_notice' },
    },
  },
  '/trainers': {
    screen: 'trainer:manage',
    functions: {
      chats: { permission: 'chat:oversee_trainers', href: '/trainers/chats' },
      view: { permission: 'trainer:view' },
      manage: { permission: 'trainer:manage' },
      salary: { permission: 'salary:view' },
    },
  },
  '/tournaments': {
    screen: 'tournament:cancel',
    functions: {
      view: { permission: 'tournament:view' },
      manage: { permission: 'tournament:manage' },
      register: { permission: 'tournament:register' },
      admitFree: { permission: 'tournament:admit_without_payment' },
      reopen: { permission: 'tournament:reopen' },
    },
  },
  '/news': {
    screen: 'news:manage',
    functions: { push: { permission: 'notification:manage' } },
  },
  '/group-sessions': {
    screen: 'group_session:manage',
    functions: {
      create: { permission: 'group_session:create' },
      publish: { permission: 'group_session:publish' },
      cancel: { permission: 'group_session:cancel' },
      register: { permission: 'group_session:register' },
      conduct: { permission: 'group_session:conduct' },
    },
  },
  '/matchmaking': {
    screen: 'matchmaking:manage',
    functions: { rating: { permission: 'rating:manage' } },
  },
  '/finance': {
    screen: 'finance:view',
    functions: {
      refund: { permission: 'payment:refund' },
      collect: { permission: 'payment:create' },
      manageExpenses: { permission: 'finance:manage' },
      approveExpense: { permission: 'finance:approve' },
      payExpense: { permission: 'finance:pay' },
      approveRefund: { permission: 'payment:refund_approve' },
      manageSalary: { permission: 'salary:manage' },
    },
  },
  '/pos': { screen: 'pos:operate' },
  '/organizations': {
    screen: 'organization:view_any',
    functions: {
      manage: { permission: 'organization:manage_any' },
    },
  },
  '/loyalty': {
    screen: 'loyalty:view_program',
    functions: {
      adjustBonuses: { permission: 'loyalty:adjust_balance' },
      adjustAdvance: { permission: 'wallet:adjust' },
      grantFree: { permission: 'subscription:grant_free' },
      manageTariffs: { permission: 'loyalty:manage_program' },
      manageBonusRules: { permission: 'loyalty:manage_program' },
      managePromocodes: { permission: 'loyalty:manage_program' },
      manageProgram: { permission: 'loyalty:manage_program' },
      manageCategories: { permission: 'loyalty:manage_program' },
      view: { permission: 'loyalty:view' },
    },
  },
  '/fitness': { screen: 'group_session:register_for_others' },
  '/access': {
    screen: 'access:check',
    functions: {
      sigurLink: { permission: 'access:manage' },
      manageZones: { permission: 'access:manage' },
      manualPass: { permission: 'access:manual_pass' },
      manageDevices: { permission: 'access:manage' },
    },
  },
  '/invite': { screen: 'client:view_any' },
  '/analytics': { screen: 'analytics:view' },
  '/activity': { screen: 'analytics:view' },
  '/staff': {
    screen: 'staff:manage',
    functions: {
      managePermissions: { permission: 'users:manage_permissions' },
    },
  },
  '/settings': {
    screen: 'settings:manage',
    functions: {
      manageCatalog: { permission: 'catalog:manage' },
      manageNotifications: { permission: 'notification:manage' },
      broadcast: { permission: 'notification:broadcast' },
      auditLog: { permission: 'audit:view' },
    },
  },
  '/subscription': { screen: 'billing:manage' },
  '/platform': { screen: 'tenant:manage' },
} as const satisfies Readonly<Record<string, Section>>;

export type SectionHref = keyof typeof SECTIONS;

export type FunctionKey<S extends SectionHref> = (typeof SECTIONS)[S] extends { readonly functions: infer F }
  ? keyof F & string
  : never;

const sectionAt = (href: string): Section | undefined => (SECTIONS as Readonly<Record<string, Section>>)[href];

export const SECTION_HREFS = Object.keys(SECTIONS) as SectionHref[];

export const screenPermission = (href: string): string | undefined => sectionAt(href)?.screen;

export const functionPermission = <S extends SectionHref>(section: S, fn: FunctionKey<S>): string => {
  const hit = sectionAt(section)?.functions?.[fn];
  if (hit === undefined) throw new Error(`В описании раздела ${section} нет функции ${fn}`);
  return hit.permission;
};

const functionsOf = (href: string): readonly SectionFunction[] => Object.values(sectionAt(href)?.functions ?? {});

const pagesOf = (href: string): readonly SectionFunction[] => functionsOf(href).filter((f) => f.href !== undefined);

export const sectionFunctionOf = (
  pathname: string,
): { section: string; href: string; permission: string } | null => {
  for (const section of SECTION_HREFS) {
    for (const f of functionsOf(section)) {
      if (f.href !== undefined && (pathname === f.href || pathname.startsWith(`${f.href}/`))) {
        return { section, href: f.href, permission: f.permission };
      }
    }
  }
  return null;
};

export const canSeeScreen = (href: string, permissions: readonly string[]): boolean => {
  const required = screenPermission(href);
  if (required === undefined) return false;
  return holds(permissions, required) || pagesOf(href).some((f) => holds(permissions, f.permission));
};

export const holdsTheScreen = (href: string, permissions: readonly string[]): boolean => {
  const required = screenPermission(href);
  return required !== undefined && holds(permissions, required);
};

export const hasAdminSurface = (permissions: readonly string[]): boolean =>
  SECTION_HREFS.some((href) => canSeeScreen(href, permissions));

export const canOpenSettingsNode = (node: SettingsNode, permissions: readonly string[]): boolean =>
  node.permission ? holds(permissions, node.permission) : canSeeScreen('/settings', permissions);

export const menuEntry = (href: string, permissions: readonly string[]): string | null => {
  if (canSeeScreen(href, permissions)) return href;
  if (href !== '/settings') return null;
  return SETTINGS_NODES.find((n) => !n.comingSoon && n.permission && canOpenSettingsNode(n, permissions))?.href ?? null;
};
